import { useEffect, useRef } from 'react'
import { useVoiceInput } from '@/hooks/use-voice-input'
import { Mic, Square, Loader2 } from 'lucide-react'

interface VoiceChatInputProps {
  /** Called with transcribed text when recording is complete */
  onTranscribed: (text: string) => void
  /** Optional: disable the button (e.g. while a message is sending) */
  disabled?: boolean
  /** Auto-start recording when mounted/prop flips to true */
  autoStart?: boolean
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Mic button for voice input in the AI chat.
 * Tap to start recording, tap again to stop and transcribe.
 * Transcribed text is passed to onTranscribed callback.
 */
export function VoiceChatInput({ onTranscribed, disabled, autoStart }: VoiceChatInputProps) {
  const { state, elapsed, error, startRecording, stopAndTranscribe, cancel } = useVoiceInput()
  const autoStartedRef = useRef(false)

  useEffect(() => {
    if (autoStart && state === 'idle' && !disabled && !autoStartedRef.current) {
      autoStartedRef.current = true
      startRecording()
    }
    if (!autoStart) {
      autoStartedRef.current = false
    }
  }, [autoStart, state, disabled, startRecording])

  const handleClick = async () => {
    if (state === 'idle') {
      await startRecording()
    } else if (state === 'recording') {
      const text = await stopAndTranscribe()
      if (text) {
        onTranscribed(text)
      }
    }
  }

  return (
    <div className="flex items-center gap-2">
      {state === 'recording' && (
        <div className="flex items-center gap-1.5">
          <div className="bg-coral h-2 w-2 animate-pulse rounded-full" />
          <span className="text-muted-foreground text-xs tabular-nums">{formatTime(elapsed)}</span>
          <button
            type="button"
            onClick={cancel}
            className="text-muted-foreground hover:text-foreground text-xs"
            aria-label="Cancel recording"
          >
            ✕
          </button>
        </div>
      )}

      {state === 'transcribing' && (
        <span className="text-muted-foreground text-xs">Transcribing…</span>
      )}

      {error && <span className="text-destructive text-xs">{error}</span>}

      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || state === 'transcribing'}
        className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
          state === 'recording'
            ? 'bg-coral text-coral-foreground hover:bg-coral/90'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        } disabled:opacity-50`}
        aria-label={
          state === 'recording'
            ? 'Stop recording'
            : state === 'transcribing'
              ? 'Transcribing'
              : 'Start voice input'
        }
      >
        {state === 'transcribing' ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : state === 'recording' ? (
          <Square className="h-4 w-4" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </button>
    </div>
  )
}
